import { useCallback, useState } from 'react'

import { captureException } from '@sentry/nextjs'

import { OrderPayloadAdapter } from '~/booking/types'
import { setOrderActionCreator } from '~/redux/slices/order'
import { setPaymentIntent } from '~/redux/slices/checkout'
import { useAddOrderMutation } from '~/redux/apis/order-api'
import { useAppDispatch, useAppSelector } from '~/redux/hooks'
import { useCreateIntentMutation } from '~/redux/apis/payment-api'
import useLogEcommerce from '~/booking/hooks/useLogEcommerce'
import { selectCurrentUser } from '~/redux/slices/user'
import todAnalytics from '~/analytics/tod/todAnalytics'

export default function useProcessCartItems(): {
  isError: boolean
  isLoading: boolean
  isUninitialized: boolean
  isSuccess: boolean
  processCartItems: (adapter: OrderPayloadAdapter) => Promise<void>
} {
  const [addOrder, { isUninitialized }] = useAddOrderMutation()
  const [createIntent] = useCreateIntentMutation()
  const [isError, setIsError] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [isSuccess, setIsSuccess] = useState(false)
  const dispatch = useAppDispatch()
  const { logCheckout, logTodFailure } = useLogEcommerce()
  const user = useAppSelector(selectCurrentUser)

  const processCartItems = useCallback(
    async (adapter: OrderPayloadAdapter): Promise<void> => {
      setIsError(false)
      setIsLoading(true)
      setIsSuccess(false)

      try {
        const orderPayload = adapter.createOrderPayload()
        const order = await addOrder(orderPayload).unwrap()
        const orderId = order.orderId

        if (orderId) {
          todAnalytics.logEvent({ event: 'order_created_hom', order })
          try {
            const paymentIntent = await createIntent({ orderId }).unwrap()
            dispatch(setOrderActionCreator(order))
            dispatch(setPaymentIntent(paymentIntent))

            setIsSuccess(true)
            logCheckout(order)
          } catch (error) {
            setIsError(true)
            const message = 'payment_intent_failed'
            logTodFailure(message, { ...order, error: error?.message })
            captureException(error, {
              user: { id: user?.id },
              tags: {
                feature: 'tod',
                message,
              },
              extra: {
                order,
              },
            })
          }
        }
      } catch (error) {
        setIsError(true)

        const message = 'failed_to_checkout'
        logTodFailure(message, { error: error?.message })
        captureException(error, {
          user: { id: user?.id },
          tags: {
            feature: 'tod',
            message,
          },
        })
      } finally {
        setIsLoading(false)
      }
    },
    [addOrder, createIntent, dispatch, logCheckout, logTodFailure, user],
  )

  return {
    isError,
    isLoading,
    isSuccess,
    isUninitialized,
    processCartItems,
  }
}
